/**
 * Match — web port of iOS
 * `client-native/.../components/PersonalizedMatch.tsx`.
 *
 * Row-aligned pairing: each row holds a Spanish button (left) and an English
 * button (right). Both sides are shuffled independently, rejecting trivially
 * aligned shuffles via `areArraysAligned` so the answer isn't a straight read
 * across a row. Two-column tap model: select one side, then tap the other to
 * attempt a match. Wrong → red flash + shake → reset after 800ms
 * (`BUTTON_RESET_DELAY`). Win →
 *   - feed: auto onFinish() immediately (iOS PersonalizedMatch:165).
 *   - lesson: 600ms → CorrectDrawer (confirm-to-continue) → onFinish(false)
 *     (iOS PersonalizedMatch:167 + CorrectDrawer 424-431). Multiple pairs, so
 *     no single translation line — the drawer is title-only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MatchActivity, MatchData } from "@shared_types";
import ActivityButton, {
  type ActivityButtonRef,
} from "../ui/ActivityButton";
import CorrectDrawer from "../ui/CorrectDrawer";
import { shuffleArray } from "@/lib/utils";
import type { ActivityComponentContract } from "../types";
import { activityContainerStyle, centeredTitleStyle } from "./layout";

const BUTTON_RESET_DELAY = 800;
/** Lesson: hold the green flash before sliding up the CorrectDrawer (iOS 600ms). */
const LESSON_CORRECT_DRAWER_DELAY_MS = 600;

type CellState = "start" | "selected" | "incorrect" | "matched";

interface MatchCell {
  id: number;
  spanish: string;
  english: string;
  state: CellState;
}

export interface MatchProps extends ActivityComponentContract {
  data: MatchActivity;
}

/** Reject shuffles where any pair sits at the same row index (too trivial). */
function areArraysAligned(a: MatchCell[], b: MatchCell[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].spanish === b[i].spanish) return true;
  }
  return false;
}

function createShuffledStates(matchData: MatchData[]): {
  spanish: MatchCell[];
  english: MatchCell[];
} {
  if (!matchData || matchData.length === 0) {
    return { spanish: [], english: [] };
  }
  let spanish: MatchCell[] = [];
  let english: MatchCell[] = [];
  let attempts = 0;
  do {
    spanish = shuffleArray(matchData).map((d, i) => ({
      id: i,
      spanish: d.spanish,
      english: d.english,
      state: "start" as CellState,
    }));
    english = shuffleArray(matchData).map((d, i) => ({
      id: i,
      spanish: d.spanish,
      english: d.english,
      state: "start" as CellState,
    }));
    attempts++;
  } while (areArraysAligned(spanish, english) && attempts < 50);
  return { spanish, english };
}

export default function Match({
  data,
  variant = "lesson",
  onFinish,
  onTrackAttempt,
}: MatchProps) {
  const isFeed = variant === "feed";

  const init = useMemo(
    () => createShuffledStates(data?.matchData ?? []),
    [data],
  );

  const [spanishCells, setSpanishCells] = useState<MatchCell[]>(init.spanish);
  const [englishCells, setEnglishCells] = useState<MatchCell[]>(init.english);
  const [finished, setFinished] = useState(false);
  const [showCorrectDrawer, setShowCorrectDrawer] = useState(false);

  const lastClicked = useRef<MatchCell | null>(null);
  const lastType = useRef<"es" | "en" | null>(null);
  // Per-activity attempt counter (1,2,3…) → attempt_number / is_first_attempt.
  const attemptRef = useRef(0);
  const spanishRefs = useRef<Record<number, ActivityButtonRef | null>>({});
  const englishRefs = useRef<Record<number, ActivityButtonRef | null>>({});

  const matchedCount = englishCells.filter((c) => c.state === "matched").length;
  const win = englishCells.length > 0 && matchedCount === englishCells.length;

  useEffect(() => {
    if (win && !finished) {
      setFinished(true);
      // Feed: advance immediately. Lesson: hold then show the CorrectDrawer.
      if (isFeed) onFinish();
      else
        window.setTimeout(
          () => setShowCorrectDrawer(true),
          LESSON_CORRECT_DRAWER_DELAY_MS,
        );
    }
  }, [win, finished, isFeed, onFinish]);

  const setSpanishState = (id: number, s: CellState) =>
    setSpanishCells((prev) =>
      prev.map((c) => (c.id === id ? { ...c, state: s } : c)),
    );
  const setEnglishState = (id: number, s: CellState) =>
    setEnglishCells((prev) =>
      prev.map((c) => (c.id === id ? { ...c, state: s } : c)),
    );

  /**
   * Revert a cell to "start" ONLY if it's still "incorrect" at the moment this
   * runs — read current state inside the functional updater so a cell that got
   * re-matched during the 800ms reset window is left untouched. `setThis` is
   * the same setter used by the rest of the component, so we route through the
   * raw cell-list setter to inspect the live state.
   */
  const revertIfIncorrect = (
    id: number,
    setThis: (id: number, s: CellState) => void,
  ) => {
    const apply = setThis === setSpanishState ? setSpanishCells : setEnglishCells;
    apply((prev) =>
      prev.map((c) =>
        c.id === id && c.state === "incorrect" ? { ...c, state: "start" } : c,
      ),
    );
  };

  const resolveMatch = (
    matches: boolean,
    aId: number,
    bId: number,
    aRef: ActivityButtonRef | null,
    bRef: ActivityButtonRef | null,
    setA: (id: number, s: CellState) => void,
    setB: (id: number, s: CellState) => void,
  ) => {
    onTrackAttempt?.("match", matches, ++attemptRef.current);
    if (matches) {
      aRef?.triggerCorrect();
      bRef?.triggerCorrect();
      setA(aId, "matched");
      setB(bId, "matched");
    } else {
      aRef?.triggerIncorrect();
      bRef?.triggerIncorrect();
      setA(aId, "incorrect");
      setB(bId, "incorrect");
      // Only revert cells that are STILL "incorrect" when the timer fires. A
      // user can re-select an incorrect cell and correctly match it within the
      // 800ms window; unconditionally resetting to "start" would clobber that
      // "matched" state and soft-lock the activity (the pair can never win).
      window.setTimeout(() => {
        revertIfIncorrect(aId, setA);
        revertIfIncorrect(bId, setB);
      }, BUTTON_RESET_DELAY);
    }
    lastClicked.current = null;
    lastType.current = null;
  };

  const handleSide = (
    cell: MatchCell,
    side: "es" | "en",
    setThis: (id: number, s: CellState) => void,
    refsThis: Record<number, ActivityButtonRef | null>,
    refsOther: Record<number, ActivityButtonRef | null>,
    setOther: (id: number, s: CellState) => void,
  ) => {
    if (cell.state === "matched") return;

    // Tap the already-selected cell again to deselect.
    if (cell.state === "selected" && lastType.current === side) {
      setThis(cell.id, "start");
      lastClicked.current = null;
      lastType.current = null;
      return;
    }
    // Switch selection within the same side.
    if (lastType.current === side && lastClicked.current) {
      setThis(lastClicked.current.id, "start");
      setThis(cell.id, "selected");
      lastClicked.current = cell;
      lastType.current = side;
      return;
    }
    // Nothing selected yet → select this cell.
    if (!lastClicked.current) {
      setThis(cell.id, "selected");
      lastClicked.current = cell;
      lastType.current = side;
      return;
    }
    // Other side selected → attempt a match.
    const other = lastClicked.current;
    const matches =
      side === "es"
        ? other.english === cell.english
        : other.spanish === cell.spanish;
    resolveMatch(
      matches,
      cell.id,
      other.id,
      refsThis[cell.id] ?? null,
      refsOther[other.id] ?? null,
      setThis,
      setOther,
    );
  };

  return (
    <div
      style={{
        ...activityContainerStyle(isFeed),
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={centeredTitleStyle(isFeed)}>Match the words</div>
      <div
        style={{
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {spanishCells.map((sp) => {
          const en = englishCells.find((e) => e.id === sp.id);
          return (
            <div
              key={`pair-${sp.id}`}
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 20,
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <ActivityButton
                  ref={(r) => {
                    spanishRefs.current[sp.id] = r;
                  }}
                  text={sp.spanish}
                  textVariant="small"
                  variant={variant}
                  disabled={sp.state === "matched"}
                  state={mapCellState(sp.state)}
                  correctAnimationHoldMs={null}
                  onPress={() =>
                    handleSide(
                      sp,
                      "es",
                      setSpanishState,
                      spanishRefs.current,
                      englishRefs.current,
                      setEnglishState,
                    )
                  }
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {en && (
                  <ActivityButton
                    ref={(r) => {
                      englishRefs.current[en.id] = r;
                    }}
                    text={en.english}
                    textVariant="small"
                    variant={variant}
                    disabled={en.state === "matched"}
                    state={mapCellState(en.state)}
                    correctAnimationHoldMs={null}
                    onPress={() =>
                      handleSide(
                        en,
                        "en",
                        setEnglishState,
                        englishRefs.current,
                        spanishRefs.current,
                        setSpanishState,
                      )
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isFeed && (
        <CorrectDrawer
          visible={showCorrectDrawer}
          hideTranslation
          onContinue={() => {
            setShowCorrectDrawer(false);
            onFinish(false);
          }}
        />
      )}
    </div>
  );
}

function mapCellState(
  s: CellState,
): "default" | "selected" | "incorrect" | "matched" {
  return s === "start" ? "default" : s;
}
